import React, { ChangeEvent, useEffect, useState } from 'react'

/**
 * Parse user-typed decimal text. Tolerates surrounding/internal spaces and a comma
 * decimal separator (Norwegian keyboards); anything that is not a plain finite
 * number parses to null. Note: '' must be rejected before Number() (Number('') === 0).
 */
export const parseDecimalText = (text: string): number | null => {
  const normalized: string = text.replace(/\s+/g, '').replace(',', '.')
  if (normalized === '') return null
  const value: number = Number(normalized)
  return Number.isFinite(value) ? value : null
}

export interface NumberInputProps {
  /** Committed value; null/undefined renders an empty field. */
  value: number | null | undefined
  /** Called with every VALID parse while typing (live updates), and never with junk. */
  onCommit: (value: number) => void
  /** If given, clearing the field commits "no value" (optional inputs). Without it, an
   *  empty field commits nothing and blur restores the last committed value. */
  onClear?: () => void
  /** Round committed values to whole numbers (counts, leg numbers, …). */
  integer?: boolean
  /** Commit only on blur/Enter instead of per keystroke. Use for consequential setters
   *  (e.g. a span-length change that confirms/removes out-of-range loads) where an
   *  intermediate value like the "1" while typing "12" must never be applied. */
  commitOnBlur?: boolean
  className?: string
  placeholder?: string
  readOnly?: boolean
  disabled?: boolean
  id?: string
  title?: string
  style?: React.CSSProperties
  'data-testid'?: string
}

/**
 * Free-typing numeric field. A number-typed controlled input fights the user: it eats
 * a leading '-', swallows '0.' and trailing decimals, cannot be cleared when a value
 * is committed, and the browser adds spinner/scroll-wheel/min-max meddling on top.
 * This field is plain text the USER owns while it has focus — nothing rewrites,
 * clamps or auto-fills it mid-typing. Every keystroke that parses as a number commits
 * immediately (so previews and visualizations stay live); the display only re-syncs
 * from the committed value when the field is NOT focused (external resets,
 * populate-for-edit) and on blur, which also reverts unparseable leftovers.
 * `inputMode="decimal"` keeps the numeric keyboard on touch devices.
 */
const NumberInput: React.FC<NumberInputProps> = ({ value, onCommit, onClear, integer, commitOnBlur, className, placeholder, readOnly, disabled, id, title, style, 'data-testid': testId }) => {
  const format = (formatValue: number | null | undefined): string => (formatValue === null || formatValue === undefined ? '' : String(formatValue))
  const [text, setText] = useState<string>(() => format(value))
  const [focused, setFocused] = useState<boolean>(false)

  // Re-sync the display from the committed value only while not editing.
  /* eslint-disable-next-line react-hooks/set-state-in-effect -- intentional display sync with the committed value */
  useEffect(() => { if (!focused) setText(format(value)) }, [value, focused])

  const commitText = (currentText: string): void => {
    if (currentText.trim() === '') {
      onClear?.()
      return
    }
    const parsed: number | null = parseDecimalText(currentText)
    if (parsed !== null) onCommit(integer ? Math.round(parsed) : parsed)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const nextText: string = event.target.value
    setText(nextText)
    if (!commitOnBlur) commitText(nextText)
  }

  const handleBlur = (): void => {
    if (commitOnBlur) commitText(text)
    setFocused(false)
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      id={id}
      className={className ?? 'form-control'}
      value={text}
      placeholder={placeholder}
      readOnly={readOnly}
      disabled={disabled}
      title={title}
      style={style}
      data-testid={testId}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      onKeyDown={(event) => { if (event.key === 'Enter') (event.target as HTMLInputElement).blur() }}
    />
  )
}

export default NumberInput
